import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Skeleton, Stack } from '@chakra-ui/react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { FaceliftScrollContainer } from '@/components/Atoms/FaceliftScrollContainer';
import FaceliftText from '@/components/Atoms/FaceliftText/FaceliftText';
import Show from '@/components/Molecules/Show';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import { formatChartNumber } from '@/utils';
import type { GradientBarChartProps } from './GradientBarChart.types';

interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  disabledSeries: Set<string>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, disabledSeries }) => {
  if (!active || !payload) return null;

  // Filter out disabled series from the tooltip
  const filteredPayload = payload.filter((entry) => {
    return !disabledSeries.has(String(entry.name));
  });

  if (filteredPayload.length === 0) return null;

  return (
    <Box p={3} borderRadius="md" borderWidth="1px" boxShadow="lg" _dark={{ bg: 'darkModeBgColor', borderColor: 'gray.600' }} _light={{ bg: 'white', borderColor: 'gray.200' }}>
      <FaceliftText fontSize="sm" fontWeight="semibold" mb={2} _dark={{ color: 'white' }} _light={{ color: 'gray.800' }}>
        {label}
      </FaceliftText>
      <Stack gap={1}>
        {filteredPayload.map((entry) => (
          <Stack key={`${label}-${entry.name}`} direction="row" align="center" gap={2}>
            <Box width={2} height={2} borderRadius="full" bg={entry.color} />
            <FaceliftText fontSize="xs" _dark={{ color: 'gray.300' }} _light={{ color: 'gray.600' }}>
              {entry.name}: {entry.value?.toLocaleString()}
            </FaceliftText>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const GradientBarChart: React.FC<GradientBarChartProps> = ({
  title = '',
  data: propData,
  isLoading = false,
  ...rest
}) => {
  // Default color palette for series
  const defaultColors = [colors.success, colors.axolotlBlue];

  // Transform the nested data structure into flat format and derive series
  const { transformedData, series, yDomain, yTicks } = useMemo(() => {
    if (!propData || propData.length === 0) {
      return {
        transformedData: [],
        series: [],
        yDomain: [0, 'auto'] as [number, string],
        yTicks: [] as number[],
      };
    }

    // Extract all unique titles (series names) from the data
    const titlesSet = new Set<string>();
    propData.forEach((category) => {
      category.data.forEach((item) => {
        titlesSet.add(item.title);
      });
    });

    const titles = Array.from(titlesSet);

    // Create series configuration with colors
    const seriesConfig = titles.map((title, index) => ({
      name: title,
      color: defaultColors[index % defaultColors.length],
    }));

    // Transform data into flat structure
    let maxValue = 0;
    const flatData = propData.map((category) => {
      const dataPoint: Record<string, string | number> = { name: category.category };

      // Add each series value to the data point
      category.data.forEach((item) => {
        dataPoint[item.title] = item.value;
        maxValue = Math.max(maxValue, item.value);
      });

      // Fill in missing series with 0
      titles.forEach((title) => {
        if (!(title in dataPoint)) {
          dataPoint[title] = 0;
        }
      });

      return dataPoint;
    });

    // Simple step chooser: always return 5 ticks (0 + 4 evenly spaced)
    const pickStep = (max: number): number => {
      if (max <= 200) return 50;
      const candidates = [50, 100, 150, 200];
      const rounded = Math.ceil(max / 10) * 10;
      const found = candidates.find((s) => s * 4 >= rounded);
      if (found) return found;
      return Math.ceil((rounded / 4) / 50) * 50; // keep multiples of 50
    };

    const stepBase = maxValue <= 200 ? maxValue : maxValue * 1.1;
    const step = pickStep(stepBase);
    const top = step * 4;
    const ticks = Array.from({ length: 5 }, (_, i) => i * step);

    return {
      transformedData: flatData,
      series: seriesConfig,
      yDomain: [0, top] as [number, number],
      yTicks: ticks,
    };
  }, [propData]);

  // State to track disabled series
  const [disabledSeries, setDisabledSeries] = useState<Set<string>>(new Set());

  const chart = useChart({ data: transformedData, series });
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  // Check if all series are disabled
  const allSeriesDisabled = series?.every(s => disabledSeries.has(String(s.name))) || false;

  // Toggle series visibility
  const handleLegendClick = (seriesName: string) => {
    setDisabledSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seriesName)) {
        newSet.delete(seriesName);
      } else {
        newSet.add(seriesName);
      }
      return newSet;
    });
  };

  return (
    <>
      <Show when={isLoading}>
        <Box width="100%" height="340px" borderRadius="md" borderWidth="1px" borderColor={'gray.400'} p={3} pl={2} _dark={{ bg: 'darkModeBgColor' }} _light={{ bg: 'white' }} overflow="hidden" display="flex" flexDirection="column" {...rest}>
          <Skeleton height={5} width="60%" maxW="240px" mb={2} ml={4} _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
          <Box flex="1" px={2} py={2} display="flex" alignItems="flex-end" justifyContent="space-evenly" gap={{ base: 1, sm: 2, md: 3 }} height="220px" width="100%">
            {Array.from({ length: 8 }, (_, groupIndex) => (
              <Box
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
                key={groupIndex}
                display="flex"
                alignItems="flex-end"
                justifyContent="center"
                gap={{ base: 0.5, md: 1 }}
                height="100%"
                flex="0 1 auto"
                minWidth="0"
              >
                {[
                  40 + (groupIndex % 5) * 5,
                  47 + (groupIndex % 5) * 4,
                ].map((heightPercent, idx) => (
                  <Skeleton
                    // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
                    key={idx}
                    height={`${heightPercent}%`}
                    width={{ base: 3, sm: 4, md: 5, lg: 6 }}
                    borderRadius="sm"
                    borderBottomRadius="0"
                    _dark={{ bg: 'gray.700' }}
                    _light={{ bg: 'gray.200' }}
                    flexShrink={0}
                  />
                ))}
              </Box>
            ))}
          </Box>
          <Stack direction="row" justify="center" gap={4} mt={2}>
            {Array.from({ length: 2 }, (_, i) => (
              <Skeleton
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
                key={i}
                height={4}
                width={15}
                _dark={{ bg: 'gray.700' }}
                _light={{ bg: 'gray.200' }}
              />
            ))}
          </Stack>
        </Box>
      </Show>

      <Show when={!isLoading}>
        <Box width="100%" height="340px" borderRadius="md" borderWidth="1px" borderColor={'gray.400'} p={3} pl={2} _dark={{ bg: 'darkModeBgColor' }} _light={{ bg: 'white' }} overflow="hidden" display="flex" flexDirection="column" position="relative" {...rest}>
          <Show when={!!title}>
            <FaceliftText fontSize="md" fontWeight="semibold" mb={2} mt={2} ml={4} _dark={{ color: 'white' }} _light={{ color: 'gray.800' }}>
              {title}
            </FaceliftText>
          </Show>
          <Show when={allSeriesDisabled}>
            <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={10}>
              <FaceliftText fontSize="md" fontWeight="semibold" _dark={{ color: 'gray.400' }} _light={{ color: 'gray.500' }}>
                No data found
              </FaceliftText>
            </Box>
          </Show>
          <FaceliftScrollContainer height="calc(100% - 50px)" minWidth="1300px" pb={0}>
            <Box position="relative" height="100%" width="100%" display="flex" flexDirection="row">
              <Chart.Root height="100%" width="100%" chart={chart}>
                <BarChart data={chart.data} margin={{ top: 15, right: 5, left: 2, bottom: 5 }} barCategoryGap="25%" barGap={4} width={1300} height={260}>
                  <Show when={!allSeriesDisabled}>
                    <CartesianGrid strokeDasharray="0" vertical={false} stroke={colors.robotGrey} />
                  </Show>
                  <Show when={!allSeriesDisabled}>
                    <XAxis
                      dataKey={chart.key('name')}
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        style: {
                          fill: isDark ? colors.lightGray : colors.black,
                          fontSize: '12px',
                          fontWeight: 500,
                          lineHeight: '100%',
                          letterSpacing: '1%',
                          textAlign: 'center',
                          textTransform: 'capitalize',
                        },
                      }}
                      dy={1}
                      padding={{ left: 0, right: 60 }}
                    />
                  </Show>
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                    tick={!allSeriesDisabled ? {
                      style: {
                        fill: isDark ? colors.lightGray : colors.black,
                        fontSize: '10px',
                        fontWeight: 500,
                        lineHeight: '100%',
                        letterSpacing: '1%',
                        textAlign: 'right',
                      },
                    } : false}
                    tickFormatter={!allSeriesDisabled ? formatChartNumber : undefined}
                    domain={!allSeriesDisabled ? yDomain : [0, 'auto']}
                    ticks={!allSeriesDisabled ? yTicks : []}
                    orientation="left"
                  />
                  <Show when={!allSeriesDisabled}>
                    <Tooltip cursor={false} animationDuration={100} content={<CustomTooltip disabledSeries={disabledSeries} />} />
                  </Show>
                  {chart.series.map((item) => {
                    const isDisabled = disabledSeries.has(String(item.name));
                    return (
                      <Show key={String(item.name)} when={!isDisabled}>
                        <Bar dataKey={chart.key(String(item.name))} fill={String(item.color)} radius={[2, 2, 0, 0]} barSize={24} />
                      </Show>
                    );
                  })}
                </BarChart>
              </Chart.Root>
            </Box>
          </FaceliftScrollContainer>
          {/* Legend at the bottom */}
          <Box width="100%" minHeight="50px" display="flex" justifyContent="center" alignItems="center" pb={2}>
            <Stack direction="row" justify="center" align="center" gap={3} opacity={1} flexWrap="wrap">
              {series.map((seriesItem, index) => {
                const seriesName = String(seriesItem.name);
                const isDisabled = disabledSeries.has(seriesName);
                return (
                  <Stack
                    key={seriesName || `legend-${index}`}
                    direction="row"
                    align="center"
                    gap={1.5}
                    flexShrink={0}
                    cursor="pointer"
                    onClick={() => handleLegendClick(seriesName)}
                    opacity={isDisabled ? 0.4 : 1}
                    transition="opacity 0.2s"
                    _hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
                  >
                    <Box width={2.5} height={2.5} borderRadius="full" bg={String(seriesItem.color)} />
                    <FaceliftText
                      fontSize="2xs"
                      fontWeight="medium"
                      lineHeight="tight"
                      _dark={{ color: 'gray.200' }}
                      _light={{ color: 'black' }}
                      whiteSpace="nowrap"
                      textDecoration={isDisabled ? 'line-through' : 'none'}
                    >
                      {seriesName}
                    </FaceliftText>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </Show>
    </>
  );
};

export default GradientBarChart;


